// Alternative approach with HTML-based popup outside of OpenLayers
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  isValidExtent,
  MapContainer,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';

import { Feature } from 'ol';
import { LineString, Point } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Circle, Fill, Stroke, Style } from 'ol/style';
import { transform } from 'ol/proj';
import { BaseMap } from '../../../Map/BaseMap';
import { buffer as extentBuffer } from 'ol/extent';
import { cn } from '@ais-ui/utils';
import { HistoricalTrack } from '@ais-ui/api-spec/portserver';
import { parseISO } from 'date-fns';
import { useDateTimeFormat } from '../../../conversions/useDateFormat';

interface ShipTrackProps {
  trackData: HistoricalTrack | null | undefined;
  hoveredPointIndex: number | null;
  onPointHover: (index: number | null) => void;
  dataType: 'sog' | 'cog';
  className?: string;
  /** Optional custom base map component (defaults to BaseMap) */
  BaseMapComponent?: React.ComponentType;
}

interface PointProperties {
  index: number;
  timestamp: string;
  course: number;
  speed: number;
}

interface PointFeature extends Feature {
  getProperties(): {
    properties: PointProperties;
  };
}

interface MapFeatures {
  trackFeatures: Feature[];
  pointFeatures: Feature[];
  extent: number[] | null;
}

function ShipTrackGeoJSON({
  trackData,
  hoveredPointIndex,
  onPointHover,
  dataType,
}: ShipTrackProps): React.JSX.Element {
  const { map, displayProjection, dataProjection } = useOpenLayersContext();
  const pointsLayerRef = useRef<VectorLayer<Feature> | null>(null);
  const trackLayerRef = useRef<VectorLayer<Feature> | null>(null);
  const highlightLayerRef = useRef<VectorLayer<Feature> | null>(null);
  const highlightSourceRef = useRef<VectorSource<Feature> | null>(null);
  const prevTrackDataRef = useRef<HistoricalTrack | null | undefined>(null);
  const mapContainerRef = useRef<HTMLElement | null>(null);

  // State for popup position and content
  const [popupInfo, setPopupInfo] = useState<{
    visible: boolean;
    x: number;
    y: number;
    speed: number;
    course: number;
    timestamp: string;
  }>({
    visible: false,
    x: 0,
    y: 0,
    speed: 0,
    course: 0,
    timestamp: '',
  });

  // Process track data into OpenLayers features
  const { trackFeatures, pointFeatures, extent } = useMemo((): MapFeatures => {
    if (!trackData?.track?.coordinates?.length) {
      return { trackFeatures: [], pointFeatures: [], extent: null };
    }

    // Create line feature for ship's path
    const coords = trackData.track.coordinates as unknown as number[][];
    const lineCoordinates = coords.map((point) =>
      transform([point[0], point[1]], dataProjection, displayProjection),
    );

    const lineStringFeature = new Feature({
      geometry: new LineString(lineCoordinates),
      name: 'Ship Track',
    });

    // Create point features for each position in the track
    const pointFeatures = coords.map((point, index: number) => {
      const transformedCoord = transform(
        [point[0], point[1]],
        dataProjection,
        displayProjection,
      );

      return new Feature({
        geometry: new Point(transformedCoord),
        properties: {
          index,
          timestamp: point[2],
          course: point[3],
          speed: point[4],
        },
      });
    });

    // Calculate the extent of the track
    const trackGeometry = lineStringFeature.getGeometry() as LineString;
    const rawExtent = trackGeometry ? trackGeometry.getExtent() : null;

    // Buffer the extent to add some padding around the track
    const bufferedExtent = rawExtent
      ? extentBuffer(rawExtent, rawExtent[2] * 0.1)
      : null;

    return {
      trackFeatures: [lineStringFeature],
      pointFeatures,
      extent: bufferedExtent,
    };
  }, [trackData, displayProjection, dataProjection]);

  // Style for the track line
  const trackStyle = new Style({
    stroke: new Stroke({
      color: 'rgba(0, 100, 122, 0.7)',
      width: 3,
    }),
  });

  // Memoized styles for points with hover effect
  const defaultPointStyle = useMemo(
    () =>
      new Style({
        image: new Circle({
          radius: 4,
          fill: new Fill({ color: 'rgba(0, 100, 122, 0.7)' }),
          stroke: new Stroke({ color: 'white', width: 1 }),
        }),
        zIndex: 10,
      }),
    [],
  );

  const hoveredPointStyle = useMemo(
    () =>
      new Style({
        image: new Circle({
          radius: 8,
          fill: new Fill({ color: 'rgba(0, 163, 196, 0.8)' }),
          stroke: new Stroke({ color: 'white', width: 2 }),
        }),
        zIndex: 100,
      }),
    [],
  );

  // Check if track data has changed
  const hasTrackDataChanged = useCallback((): boolean => {
    if (!prevTrackDataRef.current && !trackData) return false;
    if (!prevTrackDataRef.current || !trackData) return true;

    // Simple comparison - could be enhanced for more specific change detection
    const prevCoords = prevTrackDataRef.current.track?.coordinates?.length;
    const currentCoords = trackData.track?.coordinates?.length;

    return prevCoords !== currentCoords;
  }, [trackData]);

  // Set up OpenLayers layers and interactions
  useEffect(() => {
    if (!map) return;

    // Store a reference to the map container for popup positioning
    mapContainerRef.current = map.getTargetElement();

    // Create sources and layers
    const trackSource = new VectorSource({ features: trackFeatures });
    const pointSource = new VectorSource({ features: pointFeatures });

    const trackLayer = new VectorLayer({
      source: trackSource,
      style: trackStyle,
      zIndex: 10,
    });

    const pointsLayer = new VectorLayer({
      source: pointSource,
      style: defaultPointStyle,
      zIndex: 11,
    });

    // Dedicated highlight layer - adding/removing features always triggers a re-render
    const highlightSource = new VectorSource();
    const highlightLayer = new VectorLayer({
      source: highlightSource,
      style: hoveredPointStyle,
      zIndex: 12,
    });

    pointsLayerRef.current = pointsLayer;
    trackLayerRef.current = trackLayer;
    highlightLayerRef.current = highlightLayer;
    highlightSourceRef.current = highlightSource;

    // Add layers to map
    map.addLayer(trackLayer);
    map.addLayer(pointsLayer);
    map.addLayer(highlightLayer);

    // Only zoom to extent if track data has changed
    const dataChanged = hasTrackDataChanged();

    if (extent && isValidExtent(extent) && dataChanged) {
      setTimeout(() => {
        map.getView().fit(extent, {
          padding: [50, 50, 50, 50], // Add padding around the extent
          duration: 500, // Smooth animation
        });
      }, 100);
    }

    // Update the reference to current track data
    prevTrackDataRef.current = trackData;

    // Cleanup function
    return () => {
      map.removeLayer(trackLayer);
      map.removeLayer(pointsLayer);
      map.removeLayer(highlightLayer);
      pointsLayerRef.current = null;
      trackLayerRef.current = null;
      highlightLayerRef.current = null;
      highlightSourceRef.current = null;
    };
  }, [map, trackFeatures, pointFeatures, extent]); // Only depend on features and extent

  // Set up hover interaction separately (only when map changes)
  useEffect(() => {
    if (!map || !pointsLayerRef.current) return;

    const pointsLayer = pointsLayerRef.current;

    const handlePointerMove = (e: any) => {
      const pixel = map.getEventPixel(e.originalEvent);
      const hit = map.hasFeatureAtPixel(pixel, {
        layerFilter: (layer) => layer === pointsLayer,
        hitTolerance: 16,
      });

      if (hit) {
        map.getTargetElement().style.cursor = 'pointer';
        const features = map.getFeaturesAtPixel(pixel, {
          layerFilter: (layer) => layer === pointsLayer,
          hitTolerance: 16,
        });

        if (features?.length) {
          const feature = features[0] as PointFeature;
          const properties = feature.getProperties().properties;
          const index = properties.index;

          // Get screen coordinates for the popup
          const coordinate = map.getCoordinateFromPixel(pixel);
          const pixelPosition = map.getPixelFromCoordinate(coordinate);

          // Update popup state with position and data
          if (pixelPosition) {
            setPopupInfo({
              visible: true,
              x: pixelPosition[0],
              y: pixelPosition[1],
              speed: properties.speed,
              course: properties.course,
              timestamp: properties.timestamp,
            });
          }

          onPointHover(index);
        }
      } else {
        map.getTargetElement().style.cursor = '';
        setPopupInfo((prev) => ({ ...prev, visible: false }));
        onPointHover(null);
      }
    };

    map.on('pointermove', handlePointerMove);

    return () => {
      map.un('pointermove', handlePointerMove);
    };
  }, [map, onPointHover]); // Only depend on map and callback

  // Update highlight layer and popup when hoveredPointIndex changes
  useEffect(() => {
    if (!map || !highlightSourceRef.current) return;

    // Clear previous highlight
    highlightSourceRef.current.clear();

    // Add hovered feature to the highlight layer (triggers OL re-render)
    if (
      hoveredPointIndex !== null &&
      pointFeatures.length > hoveredPointIndex
    ) {
      const feature = pointFeatures[hoveredPointIndex];
      const properties = (feature as PointFeature).getProperties().properties;

      // Clone geometry for the highlight layer
      const geometry = feature.getGeometry() as Point;
      const highlightFeature = new Feature({
        geometry: geometry.clone(),
      });
      highlightSourceRef.current.addFeature(highlightFeature);

      // Convert map coordinates to screen coordinates for popup
      const coordinate = geometry.getCoordinates();
      const pixelPosition = map.getPixelFromCoordinate(coordinate);

      if (pixelPosition) {
        setPopupInfo({
          visible: true,
          x: pixelPosition[0],
          y: pixelPosition[1],
          speed: properties.speed,
          course: properties.course,
          timestamp: properties.timestamp,
        });
      }
    } else if (hoveredPointIndex === null) {
      // Hide popup when no point is hovered
      setPopupInfo((prev) => ({ ...prev, visible: false }));
    }
  }, [hoveredPointIndex, pointFeatures, map]);

  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const isoDate = popupInfo.timestamp ? parseISO(popupInfo.timestamp) : null;
  const date = formatDateTimeWithTimeZone(isoDate);

  // Render the popup outside of OpenLayers as a React component
  return (
    <>
      {popupInfo.visible && (
        <div
          className="bg-popover text-popover-foreground border-border absolute z-[1000] min-w-[100px] rounded-lg border px-2.5 py-1.5 text-xs shadow-md pointer-events-none"
          style={{
            left: `${popupInfo.x}px`,
            top: `${popupInfo.y - 20}px`,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div>
            <strong>
              {dataType === 'sog'
                ? `${popupInfo.speed.toFixed(1)} knots`
                : `${popupInfo.course.toFixed(1)}°`}
            </strong>
            <br />
            <span>{date}</span>
          </div>
          {/* Arrow pointing down */}
          <div className="border-t-popover absolute -bottom-2.5 left-1/2 -ml-2.5 border-l-[10px] border-r-[10px] border-t-[10px] border-l-transparent border-r-transparent" />
        </div>
      )}
    </>
  );
}

export default function ShipTrackMap({
  trackData,
  hoveredPointIndex,
  onPointHover,
  dataType,
  className,
  BaseMapComponent = BaseMap,
}: ShipTrackProps) {
  // Calculate map center based on track data (as a fallback)
  const mapCenter = useMemo((): [number, number] => {
    if (!trackData?.track?.coordinates?.length) {
      return [11, 59]; // Default center if no data
    }

    // Calculate average position of all coordinates
    const coordinates = trackData.track.coordinates as unknown as number[][];
    const sumLon: number = coordinates.reduce(
      (sum: number, point) => sum + point[0],
      0,
    );
    const sumLat: number = coordinates.reduce(
      (sum: number, point) => sum + point[1],
      0,
    );
    const count = coordinates.length;

    return [sumLon / count, sumLat / count];
  }, [trackData]);

  return (
    <div
      className={cn(
        'h-[500px] rounded-lg overflow-hidden shadow-md relative',
        className,
      )}
    >
      <MapContainer center={mapCenter} zoom={10}>
        <BaseMapComponent />
        <ShipTrackGeoJSON
          trackData={trackData}
          hoveredPointIndex={hoveredPointIndex}
          onPointHover={onPointHover}
          dataType={dataType}
        />
      </MapContainer>
    </div>
  );
}
